import {
  PublicKey,
  VersionedTransaction,
} from "@solana/web3.js";
import { getSDK as getUnlinkSDK } from "../../sdk/UnlinkInstance";
import { getSDK as getSolanaSDK } from "../../sdk/SolanaInstance";

/**
 * Fetches and parses messages from a Solana block at a given slot.
 * - Caches block data by slot to avoid redundant network requests.
 * - Tries up to MAX_RETRIES to retrieve the block if unavailable.
 * - Uses the Unlink SDK to extract and decrypt relevant messages.
 *
 * @param {Object} params
 * @param {number} params.slot - Slot number to fetch.
 * @param {Uint8Array} params.viewPublicKey - Recipient’s ratcheted view public key.
 * @param {Uint8Array} params.viewPrivateKey - Corresponding private key.
 * @param {Uint8Array} params.kyberPrivateKey - Kyber private key for decryption.
 * @param {Uint8Array} params.kyberSecret - Shared secret used in key exchange.
 * @param {Map<number, Object>} [params.blockCache] - Optional cache to reuse fetched blocks.
 * @returns {Promise<Array<Object>>} - Decrypted message objects or null if none found.
 */
export default async function fetchMatchingTransactions({ 
  slot, 
  viewPublicKey,
  viewPrivateKey,
  sessionKyberKeys, 
  blockCache = new Map(), // optional shared cache
}) {
  const unlinkSdk = getUnlinkSDK();
  const connection = getSolanaSDK();
  const UNLINK_PROGRAM_ID = new PublicKey(unlinkSdk.UNLINK_PROGRAM_ID);

  const MAX_RETRIES = 5;
  const RETRY_DELAY_MS = 500;

  let block = null;

  if (blockCache.has(slot) && blockCache.get(slot)) {
    block = blockCache.get(slot);
  } else {
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        block = await connection.getBlock(Number(slot), {
          maxSupportedTransactionVersion: 0,
          transactionDetails: "full",
          rewards: false,
          encoding: "json",
        });

        if (block) {
          blockCache.set(slot, block);
          break;
        }
      } catch (err) {
        if (attempt === MAX_RETRIES) {
          console.error("[fetchMatchingTransaction] Failed after retries:", err);
          return null;
        }
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
      }
    }
  }

  if (!block || !block.transactions) return null;

  const messages = await unlinkSdk.parseBlockForMessages({
    block,
    receiverViewKeypairs: [{
      viewPublicKey,
      viewPrivateKey,
    }],
    sessionKyberKeypairs: sessionKyberKeys,
  });

  return messages;
}